import type { FC, ReactNode } from 'react'

import { useEffect } from 'react'
import { useRouter } from 'next/router'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import {
    easeLoad,
    easeReveal,
    DURATION,
    prefersReducedMotion
} from '@/lib/motion'

type MotionRootProps = {
    children: ReactNode
}

const num = (el: Element, key: string, fallback = 0): number => {
    const raw = (el as HTMLElement).dataset[key]
    const parsed = raw === undefined ? NaN : Number.parseFloat(raw)

    return Number.isFinite(parsed) ? parsed : fallback
}

/**
 * Drives every entrance on the site.
 *
 * data-load      page-load choreography, runs immediately (hero + chrome)
 * data-reveal    scroll choreography, runs as the element enters the viewport
 * data-parallax  scroll-linked vertical drift, scrubbed against scroll position
 * data-hero-media hero artwork, scales and drifts as the hero leaves
 */
export const MotionRoot: FC<MotionRootProps> = ({ children }): ReactNode => {
    const router = useRouter()

    /* Hash links must not replay the page-load choreography. */
    const routeKey = router.asPath.split('#')[0]

    useEffect(() => {
        /* globals.css already forces every entrance to its resting state here. */
        if (prefersReducedMotion()) return

        gsap.registerPlugin(ScrollTrigger)

        const ctx = gsap.context(() => {
            /* ---------------------------------------------------- page load */
            const loaders = gsap.utils.toArray<HTMLElement>('[data-load]')

            loaders.forEach((el) => {
                const kind = el.dataset.load ?? 'up'
                const delay = num(el, 'loadDelay', 0)
                const duration = num(el, 'loadDuration', kind === 'up' ? DURATION.copy : DURATION.frame)
                const to = num(el, 'loadOpacity', 1)

                const from: gsap.TweenVars = { opacity: 0.001 }

                if (kind === 'up') from.y = 40
                if (kind === 'rise') { from.y = 88; from.opacity = 1 }
                if (kind === 'down') { from.y = -88; from.opacity = 1 }
                if (kind === 'left') from.x = -88
                if (kind === 'right') from.x = 88
                if (kind === 'scale') from.scale = 0.8

                gsap.fromTo(el, from, {
                    opacity: to,
                    x: 0,
                    y: 0,
                    scale: 1,
                    delay,
                    duration,
                    ease: easeLoad,
                    onComplete: () => {
                        el.style.willChange = 'auto'
                    }
                })
            })

            /* ------------------------------------------------ scroll reveal */
            const reveals = gsap.utils.toArray<HTMLElement>('[data-reveal]')

            reveals.forEach((el) => {
                const kind = el.dataset.reveal ?? 'up'
                const delay = num(el, 'revealDelay', 0)
                const duration = num(el, 'revealDuration', kind === 'fade' ? DURATION.grid : DURATION.reveal)
                const distance = num(el, 'revealDistance', 60)

                gsap.fromTo(el, {
                    opacity: 0.001,
                    y: kind === 'fade' ? 0 : distance
                }, {
                    opacity: 1,
                    y: 0,
                    delay,
                    duration,
                    ease: easeReveal,
                    onComplete: () => {
                        el.style.willChange = 'auto'
                    },
                    scrollTrigger: {
                        trigger: el,
                        start: 'top 88%',
                        once: true
                    }
                })
            })

            /* ------------------------------------------------- scroll drift */
            gsap.utils.toArray<HTMLElement>('[data-parallax]').forEach((el) => {
                const strength = num(el, 'parallax', 60)

                gsap.fromTo(el, { yPercent: -strength / 10 }, {
                    yPercent: strength / 10,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: el.parentElement ?? el,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: true
                    }
                })
            })

            /* --------------------------------------------- hero scroll exit */
            gsap.utils.toArray<HTMLElement>('[data-hero-media]').forEach((el) => {
                gsap.to(el, {
                    scale: 1.14,
                    yPercent: 8,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: el.closest('section') ?? el,
                        start: 'top top',
                        end: 'bottom top',
                        scrub: true
                    }
                })
            })

            gsap.utils.toArray<HTMLElement>('[data-hero-copy]').forEach((el) => {
                gsap.to(el, {
                    yPercent: -18,
                    opacity: 0.2,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: el.closest('section') ?? el,
                        start: 'center center',
                        end: 'bottom top',
                        scrub: true
                    }
                })
            })

            /* ------------------------------- pinned gallery panel entrance */
            gsap.utils.toArray<HTMLElement>('[data-gallery-panel]').forEach((el) => {
                const section = el.closest('section') ?? el

                gsap.fromTo(el, { yPercent: -8, scale: 0.8, opacity: 0 }, {
                    yPercent: 0,
                    scale: 1,
                    opacity: 1,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: section,
                        start: 'top bottom',
                        end: 'top top',
                        scrub: 0.6
                    }
                })
            })

        })

        const refresh = window.setTimeout(() => ScrollTrigger.refresh(), 320)

        return () => {
            window.clearTimeout(refresh)
            ctx.revert()
        }
    }, [routeKey])

    return <>{children}</>
}
